
import React from 'react'
import {Popover, Table, Tag, Tooltip} from 'antd'
import {CheckOutlined, CloseOutlined, InfoCircleOutlined, SwapRightOutlined, SwapLeftOutlined,
        MailOutlined, EyeOutlined, ExclamationCircleOutlined, StopOutlined, LinkOutlined} from '@ant-design/icons'

import styles from './Table.css'
import moment from "moment";


type Props = {
    emails: [],
    componentView: boolean,
    additionalFilter: {},
    fetching: boolean,
    sorting: {},
    pagination: {},
    onChangeTable: () => void,
    onClickRow: () => void,
}

export default class extends React.PureComponent<Props> {

    render() {

        const {
            emails,
            componentView,
            fetching,
            additionalFilter,
            sorting,
            pagination,
            onChangeTable,
            onClickRow,
        } = this.props

        const columns = [
            {
                title: 'Type',
                dataIndex: 'direction',
                key: 'direction',
                width: 60,
                sorter: false,
                render: (text, record) => (
                    <div style={{ textAlign: 'center' }}>
                        {record.direction === 'outbound' && (
                            <SwapRightOutlined style={{ color: '#52c41a', fontSize: 16 }} title="Sent" />
                        )}
                        {record.direction === 'inbound' && (
                            <SwapLeftOutlined style={{ color: '#1890ff', fontSize: 16 }} title="Received" />
                        )}
                        {!record.direction && (
                            <SwapRightOutlined style={{ color: '#52c41a', fontSize: 16 }} title="Sent" />
                        )}
                    </div>
                ),
            },
            {
                title: 'Subject',
                dataIndex: 'subject',
                key: 'subject',
                sorter: false,
                render: (text, record) => (
                    <span>{record.subject}</span>
                ),
            },
            {
                title: 'From',
                dataIndex: 'from_email',
                key: 'from_email',
                sorter: false,
                render: (text, record) => (
                    <div>
                        {record.from_name && record.from_email ? (
                            <span title={record.from_email}>
                                {record.from_name}
                            </span>
                        ) : (
                            record.from_email || 'System'
                        )}
                    </div>
                ),
            },
            {
                title: 'To',
                dataIndex: 'to',
                key: 'to',
                sorter: false,
                render: (text, record) => {
                    const toText = record.to || '';
                    if (toText.length > 75) {
                        // Split by comma or semicolon and display with line breaks in tooltip
                        const emails = toText.split(/[,;]/).map(e => e.trim()).filter(e => e);
                        return (
                            <Tooltip
                                title={
                                    <div>
                                        {emails.map((email, index) => (
                                            <div key={index}>{email}</div>
                                        ))}
                                    </div>
                                }
                            >
                                <span style={{ cursor: 'help' }}>
                                    {toText.substring(0, 75)}...
                                </span>
                            </Tooltip>
                        );
                    }
                    return toText;
                },
            },
            {
                title: 'Status',
                dataIndex: 'status',
                sorter: false,
                render: (text, record) => (
                    <div>
                        {record.status === 'hold' && <Tag color='red'>Hold</Tag>}
                        {record.status === 'pending' && <Tag color='blue'>Pending</Tag>}
                        {record.status === 'deleted' && <Tag color='grey'>Deleted</Tag>}
                        {record.status === 'sent' && <Tag color='green'>Sent</Tag>}
                        {record.status === 'failed' && <Tag color='red'>Failed</Tag>}
                    </div>
                ),
            },
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span>Events</span>
                        <Popover
                            title="Email Event Icons"
                            content={
                                <div style={{ maxWidth: 250 }}>
                                    <div style={{ marginBottom: 8 }}>
                                        <MailOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                                        <span>Delivered</span>
                                    </div>
                                    <div style={{ marginBottom: 8 }}>
                                        <EyeOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                                        <span>Opened</span>
                                    </div>
                                    <div style={{ marginBottom: 8 }}>
                                        <LinkOutlined style={{ color: '#722ed1', marginRight: 8 }} />
                                        <span>Link Clicked</span>
                                    </div>
                                    <div style={{ marginBottom: 8 }}>
                                        <StopOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                                        <span>Bounced</span>
                                    </div>
                                    <div>
                                        <ExclamationCircleOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
                                        <span>Complaint</span>
                                    </div>
                                </div>
                            }
                            trigger="hover"
                        >
                            <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 12, cursor: 'help' }} />
                        </Popover>
                    </div>
                ),
                dataIndex: 'events',
                width: 120,
                sorter: false,
                render: (text, record) => {
                    const events = [];
                    // Delivered event
                    if (record.delivered === true) {
                        events.push(
                            <Tooltip key="delivered" title={`Delivered${record.delivered_at ? ` at ${moment(record.delivered_at).format('DD MMM HH:mm')}` : ''}`}>
                                <MailOutlined style={{ color: '#52c41a', fontSize: 16, marginRight: 4 }} />
                            </Tooltip>
                        );
                    }

                    // Opened event
                    if (record.opened === true) {
                        events.push(
                            <Tooltip key="opened" title={`Opened${record.opened_at ? ` at ${moment(record.opened_at).format('DD MMM HH:mm')}` : ''}`}>
                                <EyeOutlined style={{ color: '#1890ff', fontSize: 16, marginRight: 4 }} />
                            </Tooltip>
                        );
                    }

                    // Clicked event
                    if (record.click === true) {
                        events.push(
                            <Tooltip key="clicked" title={`Clicked${record.click_at ? ` at ${moment(record.click_at).format('DD MMM HH:mm')}` : ''}`}>
                                <LinkOutlined style={{ color: '#722ed1', fontSize: 16, marginRight: 4 }} />
                            </Tooltip>
                        );
                    }

                    // Bounced event
                    if (record.bounced === true) {
                        events.push(
                            <Tooltip key="bounced" title={`Bounced${record.bounced_at ? ` at ${moment(record.bounced_at).format('DD MMM HH:mm')}` : ''}`}>
                                <StopOutlined style={{ color: '#ff4d4f', fontSize: 16, marginRight: 4 }} />
                            </Tooltip>
                        );
                    }

                    // Complaint event
                    if (record.complaint === true) {
                        events.push(
                            <Tooltip key="complaint" title={`Complaint${record.complaint_at ? ` at ${moment(record.complaint_at).format('DD MMM HH:mm')}` : ''}`}>
                                <ExclamationCircleOutlined style={{ color: '#fa8c16', fontSize: 16, marginRight: 4 }} />
                            </Tooltip>
                        );
                    }

                    return (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {events.length > 0 ? events : (
                                <span style={{ color: '#d9d9d9', fontSize: '12px' }}>No events</span>
                            )}
                        </div>
                    );
                },
            },
            {
                title: 'Created',
                key: 'created_at',
                sorter: true,
                sortOrder: sorting.columnKey === 'created_at' ? (sorting.descending ? 'descend' : 'ascend') : null,
                render: (text, record) => (
                    <div>
                        {record.created_at ? moment(record.created_at).format('DD MMM YYYY HH:mm:ss') : ' '}
                    </div>
                ),
            },
            {
                title: 'Sent at',
                key: 'sent_at',
                sorter: true,
                render: (text, record) => (
                    <div>
                        {record.sent_at ? moment(record.sent_at).format('DD MMM YYYY HH:mm:ss') : ' '}
                    </div>
                ),
            },
            {
                title: ' ',
                key: 'body',
                fixed: 'left',
                sorter: false,
                width: 35,
                render: (text, record) => record.body && (
                    <Popover
                        title="Body"
                        trigger="hover"
                        content={(
                            <div style={{maxWidth: 300}}>
                                {record.body}
                            </div>
                        )}
                        style={{maxWidth: 300}}
                    >
                        <InfoCircleOutlined style={{color: '#1890FF', fontSize: 18}}/>
                    </Popover>
                )
            },


        ]

        /*
            {
                title: 'CC',
                dataIndex: 'cc',
                key: 'cc',
                sorter: false,
                render: (text, record) => record.cc,
            },
            {
                title: 'BCC',
                dataIndex: 'bcc',
                key: 'bcc',
                sorter: false,
                render: (text, record) => record.bcc,
            },
         */
        const scroll = { x: "max-content" };

        return (
            <Table
                scroll={scroll}
                columns={columns}
                dataSource={emails}
                rowKey='id'
                onChange={onChangeTable}
                bordered
                loading={fetching}
                className={styles.table}
                onRow={(record) => ({
                    onClick: () => onClickRow(record.id),
                })}
                rowClassName={(record) => record.direction === 'inbound' ? 'inbound-email-row' : ''}
                style={{ whiteSpace: 'pre'}}
                pagination={{
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '30', '40', '50'],
                    current: pagination.pageNumber,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                }}
                sortDirections={['descend', 'ascend']}
            />
        )

    }

}


